/**
 * ErrorBoundary.tsx
 * Composant React pour capturer les erreurs de rendu
 * Intégré avec ErrorHandlerService pour le logging centralisé
 */
import React, { Component, ReactNode } from 'react';
import { logger } from '../../../utils/ProfessionalLogger';
import { AlertTriangle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  name?: string;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary React pour capturer et afficher les erreurs
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log l'erreur avec ProfessionalLogger
    logger.error(`[ErrorBoundary:${this.props.name || 'Unknown'}]`, { error, errorInfo });

    // Intégration avec ErrorNotificationService (Phase 2.5)
    import('../../../services/ErrorNotificationService')
      .then(({ ErrorNotificationService }) => {
        ErrorNotificationService.getInstance().notifyError(error, {
          severity: 'high',
          category: 'system',
          context: {
            component: this.props.name || 'Unknown',
            action: 'render',
            errorInfo: errorInfo.componentStack,
          },
        });
      })
      .catch(() => {
        // Fallback si l'import échoue - utiliser ErrorHandlerService
        import('../../../services/ErrorHandlerService')
          .then(({ ErrorHandlerService }) => {
            ErrorHandlerService.getInstance().handleError(
              error,
              {
                component: this.props.name || 'Unknown',
                action: 'render',
              },
              'high'
            );
          })
          .catch(() => {
            logger.error('Failed to load error handling services');
          });
      });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Afficher le fallback personnalisé ou le fallback par défaut
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="error-boundary-fallback"
          style={{
            padding: '24px',
            margin: '16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: '48px',
              marginBottom: '16px',
            }}
          >
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h3
            style={{
              color: '#991b1b',
              marginBottom: '8px',
              fontSize: '18px',
              fontWeight: 600,
            }}
          >
            Une erreur est survenue
          </h3>
          <p
            style={{
              color: '#b91c1c',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            {this.state.error?.message || 'Erreur inconnue'}
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Réessayer
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
